/**
 * Static table of SQLite built-in functions for client-side signature help.
 *
 * SQLite has no catalog for introspecting function signatures (user-defined
 * functions registered via sqlite3_create_function are opaque). This module
 * provides a hard-coded map of the most commonly used built-ins sourced from
 * https://sqlite.org/lang_corefunc.html, https://sqlite.org/lang_datefunc.html,
 * and https://sqlite.org/json1.html.
 *
 * The map is keyed by lowercase function name. Each entry carries the same
 * `RoutineMetadata` shape used by the IPC-based resolver for other engines,
 * so `computeFunctionSignatureHelp` can return it directly without conversion.
 */

import type { RoutineMetadata, RoutineParameter } from '../../../../types/electron';

function p(name: string, dataType: string): RoutineParameter {
  return { name, dataType };
}

function entry(
  name: string,
  parameters: RoutineParameter[],
  documentation?: string,
): RoutineMetadata & { documentation?: string } {
  return { schema: 'main', name, parameters, ...(documentation ? { documentation } : {}) };
}

export type SqliteBuiltinEntry = RoutineMetadata & { documentation?: string };

/**
 * Map of built-in SQLite functions. Key is the lowercase function name.
 * Minimum 25 entries covering core string, math, date/time, aggregate,
 * JSON1 extension, conditional, typeof, and random.
 */
export const SQLITE_BUILTIN_ROUTINES: ReadonlyMap<string, SqliteBuiltinEntry> = new Map([
  // --- Core string functions ---
  ['substr', entry('substr', [p('X', 'text'), p('Y', 'integer'), p('Z', 'integer')], 'Return a substring of X starting at position Y with length Z.')],
  ['substring', entry('substring', [p('X', 'text'), p('Y', 'integer'), p('Z', 'integer')], 'Alias for substr.')],
  ['length', entry('length', [p('X', 'text')], 'Return the length of string X in characters.')],
  ['upper', entry('upper', [p('X', 'text')], 'Return a copy of X with all characters converted to upper case.')],
  ['lower', entry('lower', [p('X', 'text')], 'Return a copy of X with all characters converted to lower case.')],
  ['trim', entry('trim', [p('X', 'text')], 'Remove whitespace from both ends of X.')],
  ['ltrim', entry('ltrim', [p('X', 'text')], 'Remove whitespace from the left end of X.')],
  ['rtrim', entry('rtrim', [p('X', 'text')], 'Remove whitespace from the right end of X.')],
  ['replace', entry('replace', [p('X', 'text'), p('Y', 'text'), p('Z', 'text')], 'Replace every occurrence of Y in X with Z.')],
  ['instr', entry('instr', [p('X', 'text'), p('Y', 'text')], 'Return the position of the first occurrence of Y in X.')],
  ['printf', entry('printf', [p('FORMAT', 'text'), p('...', 'any')], 'Format a string using printf-style substitution.')],
  ['hex', entry('hex', [p('X', 'blob')], 'Return the hexadecimal rendering of X.')],
  ['unicode', entry('unicode', [p('X', 'text')], 'Return the unicode code point for the first character of X.')],
  ['char', entry('char', [p('X1', 'integer'), p('X2', 'integer')], 'Return a string composed of characters from unicode code points.')],
  ['like', entry('like', [p('pattern', 'text'), p('X', 'text')], 'Pattern matching with LIKE.')],
  ['glob', entry('glob', [p('pattern', 'text'), p('X', 'text')], 'Pattern matching with Unix glob syntax.')],
  ['zeroblob', entry('zeroblob', [p('N', 'integer')], 'Return a blob consisting of N zero bytes.')],
  ['quote', entry('quote', [p('X', 'any')], 'Return the text of an SQL literal that is the value of X.')],

  // --- Math functions ---
  ['abs', entry('abs', [p('X', 'numeric')], 'Return the absolute value of X.')],
  ['round', entry('round', [p('X', 'numeric'), p('Y', 'integer')], 'Round X to Y decimal places.')],
  ['random', entry('random', [], 'Return a pseudo-random integer between -9223372036854775808 and +9223372036854775807.')],
  ['max', entry('max', [p('X', 'any'), p('Y', 'any')], 'Return the argument with the maximum value.')],
  ['min', entry('min', [p('X', 'any'), p('Y', 'any')], 'Return the argument with the minimum value.')],

  // --- Aggregate functions ---
  ['count', entry('count', [p('X', 'any')], 'Return the number of non-NULL values in X.')],
  ['sum', entry('sum', [p('X', 'numeric')], 'Return the sum of all non-NULL values in X.')],
  ['avg', entry('avg', [p('X', 'numeric')], 'Return the average value of all non-NULL values in X.')],
  ['total', entry('total', [p('X', 'numeric')], 'Return the sum of all non-NULL values, always returning a float.')],
  ['group_concat', entry('group_concat', [p('X', 'text'), p('separator', 'text')], 'Concatenate all non-NULL values of X with separator.')],

  // --- Type / conditional functions ---
  ['typeof', entry('typeof', [p('X', 'any')], 'Return the type of X as a string.')],
  ['coalesce', entry('coalesce', [p('X', 'any'), p('Y', 'any')], 'Return the first non-NULL argument.')],
  ['ifnull', entry('ifnull', [p('X', 'any'), p('Y', 'any')], 'Return X if it is not NULL, otherwise return Y.')],
  ['nullif', entry('nullif', [p('X', 'any'), p('Y', 'any')], 'Return NULL if X equals Y, otherwise return X.')],
  ['iif', entry('iif', [p('condition', 'boolean'), p('X', 'any'), p('Y', 'any')], 'If condition is true return X, otherwise return Y.')],
  ['cast', entry('cast', [p('X', 'any'), p('type', 'text')], 'Convert X to the specified type.')],

  // --- Date/time functions ---
  ['date', entry('date', [p('time_value', 'text'), p('modifier', 'text')], 'Return the date in YYYY-MM-DD format.')],
  ['time', entry('time', [p('time_value', 'text'), p('modifier', 'text')], 'Return the time as HH:MM:SS.')],
  ['datetime', entry('datetime', [p('time_value', 'text'), p('modifier', 'text')], 'Return the date and time as YYYY-MM-DD HH:MM:SS.')],
  ['julianday', entry('julianday', [p('time_value', 'text'), p('modifier', 'text')], 'Return the Julian day number.')],
  ['strftime', entry('strftime', [p('format', 'text'), p('time_value', 'text'), p('modifier', 'text')], 'Format a date/time value according to the format string.')],
  ['unixepoch', entry('unixepoch', [p('time_value', 'text'), p('modifier', 'text')], 'Return the number of seconds since 1970-01-01.')],

  // --- JSON1 extension ---
  ['json_extract', entry('json_extract', [p('json', 'text'), p('path', 'text')], 'Extract a value from a JSON string at the given path.')],
  ['json_type', entry('json_type', [p('json', 'text'), p('path', 'text')], 'Return the type of the JSON value at the given path.')],
  ['json_array_length', entry('json_array_length', [p('json', 'text'), p('path', 'text')], 'Return the number of elements in the JSON array at path.')],
  ['json_each', entry('json_each', [p('json', 'text'), p('path', 'text')], 'Table-valued function that walks the JSON value at path.')],
  ['json_valid', entry('json_valid', [p('json', 'text')], 'Return 1 if the argument is well-formed JSON, 0 otherwise.')],
  ['json_object', entry('json_object', [p('label1', 'text'), p('value1', 'any')], 'Return a JSON object from key/value pairs.')],
  ['json_array', entry('json_array', [p('value1', 'any'), p('value2', 'any')], 'Return a JSON array from the arguments.')],
  ['json_insert', entry('json_insert', [p('json', 'text'), p('path', 'text'), p('value', 'any')], 'Insert a value into JSON at path (does not replace).')],
  ['json_replace', entry('json_replace', [p('json', 'text'), p('path', 'text'), p('value', 'any')], 'Replace a value in JSON at path.')],
  ['json_set', entry('json_set', [p('json', 'text'), p('path', 'text'), p('value', 'any')], 'Set a value in JSON at path (insert or replace).')],
  ['json_remove', entry('json_remove', [p('json', 'text'), p('path', 'text')], 'Remove the value at path from the JSON string.')],
]);
